/* Configured providers, models and routing — the part of the Model Routing tab that names a provider.
 *
 * **Inside the existing tab, never on a page of its own.** The direct route still works and still
 * serves every tenant that stored one; this section adds the providers that need more than an
 * endpoint and a model name to reach.
 *
 * **One dropdown and one focused panel, not a wall of provider cards.** Twelve cards in three groups
 * made the page a catalogue to scroll past on every visit. The catalog now fills a grouped select —
 * still from the server's own catalog, so a provider added there appears here unchanged — and only
 * the chosen provider's form or management panel is rendered: the create form for one not yet
 * configured, and its models, tests and actions for one that is.
 *
 * Four things this screen must not do, each of which a simpler version would:
 *
 * - **Never show a credential.** Sensitive fields use `SecretField`, which is uncontrolled so the
 *   typed value never enters the DOM attribute; a saved credential is shown as the *names* of its
 *   fields; and leaving a sensitive field empty on an edit keeps the stored one.
 * - **Never call a saved configuration connected.** Status comes from the last real test, and a model
 *   that has not answered one cannot be activated — the button says why before the round trip.
 * - **Never switch models silently.** A fallback is chosen by name and its use is recorded.
 * - **Never paraphrase a refusal.** A 403, 409 or 422 is rendered as the server wrote it.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuditEntries } from '@/features/audit';
import type {
  AuthMethod,
  ProviderKind,
  ProviderSpec,
} from '@/shared/api/generated/ai-serving';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { OverflowMenu } from '@/shared/ui/OverflowMenu';
import { SecretField, SelectField, TextField } from '@/shared/ui/Field';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  useActivateModel,
  useCreateModel,
  useCreateProvider,
  useDeleteModel,
  useDeleteProvider,
  useProviderCatalog,
  useProviderConfigs,
  useRouting,
  useSetFallback,
  useSetRoutingEnabled,
  useTestModel,
  useTestProvider,
  useUpdateProvider,
} from '../api/providers';
import type {
  ConnectionTestResult,
  ModelConfigView,
  ProviderConfigView,
  RoutingView,
} from '../api/providers';
import {
  canActivate,
  credentialsFor,
  fieldsFor,
  initialValues,
  missingFields,
  preferredMethod,
  providerChoices,
  settingsFor,
  statusTone,
} from '../model/providers';

function StatusChip({
  status,
}: {
  readonly status: ModelConfigView['status'];
}): ReactElement {
  const { t } = useTranslation();
  const tone = statusTone(status);
  return (
    <span className={`lq-ai__chip lq-ai__chip--${tone}`}>
      <span
        className={`lq-ai__dot${status === 'untested' ? '' : ' lq-ai__dot--filled'}`}
      />
      {t(`serving.llm.status.${status}`)}
    </span>
  );
}

function when(value: string | null): string {
  return value === null ? '' : new Date(value).toLocaleString();
}

/* --- active model, routing switch, fallback --------------------------------------------------- */

function ActiveRouting({
  routing,
  providers,
}: {
  readonly routing: RoutingView;
  readonly providers: readonly ProviderConfigView[];
}): ReactElement {
  const { t } = useTranslation();
  const enable = useSetRoutingEnabled();
  const fallback = useSetFallback();
  const [fallbackId, setFallbackId] = useState(routing.fallback?.model.id ?? '');
  const candidates = providers.flatMap((provider) =>
    provider.models
      .filter((m) => m.id !== routing.active?.model.id && canActivate(m, provider))
      .map((m) => ({
        value: m.id,
        label: `${provider.display_name} · ${m.display_name}`,
      })),
  );
  const active = routing.active;
  return (
    <section
      className="lq-ai__card lq-llm__routing"
      aria-label={t('serving.llm.active.title')}
    >
      {/* Two columns: what serves the tenant now, and what stands in when it cannot be reached.
          Side by side because they are read together — "is there a safety net" is the next
          question after "what is live" — and stacked on a narrow screen. */}
      <div className="lq-llm__routing-col">
        <div className="lq-llm__routing-head">
          <h2 className="lq-llm__heading">{t('serving.llm.active.title')}</h2>
          {(active !== null || routing.mode === 'direct_route') && (
            <Button
              busy={enable.isPending}
              onClick={() => {
                enable.mutate(!routing.enabled);
              }}
              size="compact"
              tone="secondary"
            >
              {routing.enabled
                ? t('serving.llm.active.switchOff')
                : t('serving.llm.active.switchOn')}
            </Button>
          )}
        </div>
        {active === null ? (
          <p className="lq-llm__note">
            {routing.mode === 'direct_route'
              ? t('serving.llm.active.direct', {
                  model: routing.direct_route?.model ?? '',
                  origin: routing.direct_route?.endpoint_origin ?? '',
                })
              : t('serving.llm.active.none')}
          </p>
        ) : (
          <dl className="lq-llm__facts">
            <dt>{t('serving.llm.active.provider')}</dt>
            <dd>
              {active.provider_name}{' '}
              <span className="lq-ai__muted">
                ({t(`serving.llm.category.${active.category}`)})
              </span>
            </dd>
            <dt>{t('serving.llm.active.model')}</dt>
            <dd className="lq-ai__mono">
              {active.model.deployment_name || active.model.model_identifier}
            </dd>
            <dt>{t('serving.llm.active.auth')}</dt>
            <dd>{t(`serving.llm.auth.${active.auth_method}`)}</dd>
            <dt>{t('serving.llm.active.status')}</dt>
            <dd className="lq-llm__status-line">
              <StatusChip status={active.model.status} />
              <span className="lq-ai__muted">
                {when(active.model.last_tested_at) || t('serving.llm.never')}
              </span>
              {routing.enabled ? null : (
                <strong className="lq-llm__off">
                  {t('serving.llm.active.switchedOff')}
                </strong>
              )}
            </dd>
          </dl>
        )}
        {enable.isError && (
          <Alert title={t('serving.llm.failed')} tone="error">
            {enable.error.message}
          </Alert>
        )}
      </div>

      <div className="lq-llm__routing-col lq-llm__routing-col--side">
        <h3 className="lq-llm__subheading">{t('serving.llm.fallback.title')}</h3>
        <p className="lq-llm__note">{t('serving.llm.fallback.explain')}</p>
        {routing.fallback !== null && (
          <p className="lq-llm__note lq-llm__note--strong">
            {t('serving.llm.fallback.current', {
              model: routing.fallback.model.display_name,
              provider: routing.fallback.provider_name,
            })}{' '}
            {routing.fallback_enabled ? '' : t('serving.llm.fallback.disabledNote')}
          </p>
        )}
        {candidates.length === 0 ? (
          <p className="lq-llm__note">{t('serving.llm.fallback.noCandidates')}</p>
        ) : (
          <div className="lq-llm__inline">
            <SelectField
              label={t('serving.llm.fallback.model')}
              onChange={setFallbackId}
              options={[
                { value: '', label: t('serving.llm.fallback.choose') },
                ...candidates,
              ]}
              value={fallbackId}
            />
            <Button
              disabled={fallbackId === ''}
              busy={fallback.isPending}
              onClick={() => {
                fallback.mutate({ modelId: fallbackId, enabled: true });
              }}
              tone="secondary"
            >
              {t('serving.llm.fallback.set')}
            </Button>
            {routing.fallback !== null && (
              <Button
                busy={fallback.isPending}
                onClick={() => {
                  fallback.mutate({ modelId: null, enabled: false });
                }}
                tone="secondary"
              >
                {t('serving.llm.fallback.clear')}
              </Button>
            )}
          </div>
        )}
        {fallback.isError && (
          <Alert title={t('serving.llm.failed')} tone="error">
            {fallback.error.message}
          </Alert>
        )}
      </div>
    </section>
  );
}

/* --- provider editor --------------------------------------------------------------------------- */

/** The configuration form for one provider. **Built from that provider's catalog entry**, so it
 * shows only the fields this provider and this authentication method have — Gemini asks for a key,
 * Bedrock for a region and an identity, Ollama for a server URL — and never a generic form that
 * assumes every provider is the same. */
export function ProviderEditor({
  spec,
  stored,
  onCancel,
  onSaved,
}: {
  readonly spec: ProviderSpec;
  readonly stored: ProviderConfigView | undefined;
  readonly onCancel: () => void;
  readonly onSaved: (saved: ProviderConfigView) => void;
}): ReactElement {
  const { t } = useTranslation();
  const create = useCreateProvider();
  const update = useUpdateProvider();
  const [name, setName] = useState(stored?.display_name ?? spec.display_name);
  const [method, setMethod] = useState<AuthMethod>(
    stored?.auth_method ?? preferredMethod(spec),
  );
  const [values, setValues] = useState<Record<string, string>>(() =>
    initialValues(spec, stored),
  );
  /* The caller's own copy of what was typed into the secret fields — memory, never the DOM. */
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const storedFields = stored?.credential.fields ?? [];
  const missing = missingFields(spec, method, values, secrets, storedFields);
  const mutation = stored === undefined ? create : update;

  const submit = (): void => {
    const write = {
      kind: spec.kind,
      display_name: name.trim(),
      auth_method: method,
      settings: settingsFor(spec, method, values),
      credentials: credentialsFor(spec, method, secrets),
      enabled: stored?.enabled ?? true,
    };
    const done = { onSuccess: onSaved };
    if (stored === undefined) create.mutate(write, done);
    else update.mutate({ id: stored.id, write }, done);
  };

  return (
    <section
      aria-label={t('serving.llm.editor.title', { provider: spec.display_name })}
      className="lq-llm__panel"
    >
      <h3 className="lq-llm__subheading">
        {t('serving.llm.editor.title', { provider: spec.display_name })}
      </h3>
      {spec.description !== '' && <p className="lq-ai__muted">{spec.description}</p>}
      <div className="lq-llm__form">
        <TextField
          label={t('serving.llm.editor.name')}
          maxLength={120}
          onChange={setName}
          required
          value={name}
        />
        {spec.auth_methods.length > 1 && (
          <SelectField
            label={t('serving.llm.editor.auth')}
            onChange={(next: AuthMethod) => {
              setMethod(next);
              setSecrets({});
            }}
            options={spec.auth_methods.map((m) => ({
              value: m,
              label: t(`serving.llm.auth.${m}`),
            }))}
            value={method}
          />
        )}
        {fieldsFor(spec, method).map((field) =>
          field.sensitive ? (
            <SecretField
              hint={
                storedFields.includes(field.name)
                  ? t('serving.llm.editor.keepCredential')
                  : field.help || undefined
              }
              key={`${method}:${field.name}`}
              label={field.label}
              multiline={
                field.name === 'service_account_json' || field.name === 'private_key'
              }
              onChange={(value) => {
                setSecrets((current) => ({ ...current, [field.name]: value }));
              }}
              required={field.required && !storedFields.includes(field.name)}
              resetKey={`${stored?.id ?? 'new'}:${method}`}
            />
          ) : field.choices.length > 0 ? (
            <SelectField
              hint={field.help || undefined}
              key={field.name}
              label={field.label}
              onChange={(value: string) => {
                setValues((current) => ({ ...current, [field.name]: value }));
              }}
              options={field.choices.map((choice) => ({ value: choice, label: choice }))}
              value={values[field.name] ?? field.default}
            />
          ) : (
            <TextField
              hint={field.help || undefined}
              key={field.name}
              label={field.label}
              maxLength={2000}
              onChange={(value) => {
                setValues((current) => ({ ...current, [field.name]: value }));
              }}
              placeholder={field.placeholder || undefined}
              required={field.required}
              value={values[field.name] ?? ''}
            />
          ),
        )}
      </div>
      {method === 'platform_managed' && (
        <p className="lq-ai__muted">{t('serving.llm.editor.platformManaged')}</p>
      )}
      {stored !== undefined && stored.credential.configured && (
        <p className="lq-ai__muted">
          {t('serving.llm.editor.credentialStored', {
            fields: stored.credential.fields.join(', '),
          })}
        </p>
      )}
      {missing.length > 0 && (
        <p className="lq-llm__hint" role="status">
          {t('serving.llm.editor.missing', { fields: missing.join(', ') })}
        </p>
      )}
      {mutation.isError && (
        <Alert title={t('serving.llm.failed')} tone="error">
          {mutation.error.message}
        </Alert>
      )}
      <div className="lq-llm__footer">
        <Button onClick={onCancel} tone="secondary">
          {t('serving.llm.editor.cancel')}
        </Button>
        <Button
          busy={mutation.isPending}
          disabled={missing.length > 0 || name.trim() === ''}
          onClick={submit}
          tone="primary"
        >
          {stored === undefined
            ? t('serving.llm.editor.save')
            : t('serving.llm.editor.update')}
        </Button>
      </div>
    </section>
  );
}

/* --- models ------------------------------------------------------------------------------------ */

function TestOutcome({
  result,
}: {
  readonly result: ConnectionTestResult;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <p className="lq-llm__outcome" role="status">
      <StatusChip status={result.status} /> {result.detail}
      {result.latency_ms !== null &&
        ` · ${t('serving.llm.latency', { ms: result.latency_ms })}`}
    </p>
  );
}

function AddModel({
  provider,
  spec,
  onDone,
}: {
  readonly provider: ProviderConfigView;
  readonly spec: ProviderSpec;
  readonly onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const create = useCreateModel();
  const [identifier, setIdentifier] = useState('');
  const [deployment, setDeployment] = useState('');
  const [name, setName] = useState('');
  const [timeout, setTimeoutSeconds] = useState('');
  const needsDeployment = spec.deployment_label !== '';
  const incomplete =
    identifier.trim() === '' || (needsDeployment && deployment.trim() === '');
  return (
    <section
      aria-label={t('serving.llm.model.addTitle', { provider: provider.display_name })}
      className="lq-llm__panel lq-llm__panel--inset"
    >
      <h4 className="lq-llm__minorheading">
        {t('serving.llm.model.addTitle', { provider: provider.display_name })}
      </h4>
      <div className="lq-llm__form">
        <TextField
          label={spec.model_label}
          maxLength={200}
          mono
          onChange={setIdentifier}
          placeholder={spec.model_placeholder || undefined}
          required
          value={identifier}
        />
        {needsDeployment && (
          <TextField
            label={spec.deployment_label}
            maxLength={200}
            mono
            onChange={setDeployment}
            required
            value={deployment}
          />
        )}
        <TextField
          label={t('serving.llm.model.name')}
          maxLength={120}
          onChange={setName}
          value={name}
        />
        <TextField
          hint={t('serving.llm.model.timeoutHint')}
          label={t('serving.llm.model.timeout')}
          max={300}
          min={5}
          onChange={setTimeoutSeconds}
          type="number"
          value={timeout}
        />
      </div>
      {create.isError && (
        <Alert title={t('serving.llm.failed')} tone="error">
          {create.error.message}
        </Alert>
      )}
      <div className="lq-llm__footer">
        <Button onClick={onDone} tone="secondary">
          {t('serving.llm.editor.cancel')}
        </Button>
        <Button
          busy={create.isPending}
          disabled={incomplete}
          onClick={() => {
            create.mutate(
              {
                providerId: provider.id,
                write: {
                  display_name: name.trim(),
                  model_identifier: identifier.trim(),
                  deployment_name: deployment.trim(),
                  parameters:
                    timeout.trim() === '' ? {} : { timeout_seconds: Number(timeout) },
                  enabled: true,
                },
              },
              { onSuccess: onDone },
            );
          }}
          tone="primary"
        >
          {t('serving.llm.model.save')}
        </Button>
      </div>
    </section>
  );
}

/** One model, on one line. **Only what is unique to the model is a visible button.**
 *
 * The table this replaced repeated the provider's own "Test connection" and a second remove button on
 * every row — and the provider-level test *is* a test of its first model, so on a one-model provider
 * the two buttons did the same thing side by side. Activate stays inline because it is the one action
 * nothing else on the panel offers; testing and removing this specific model sit in its menu.
 */
function ModelRow({
  model,
  provider,
  routing,
}: {
  readonly model: ModelConfigView;
  readonly provider: ProviderConfigView;
  readonly routing: RoutingView;
}): ReactElement {
  const { t } = useTranslation();
  const test = useTestModel();
  const activate = useActivateModel();
  const remove = useDeleteModel();
  const [confirming, setConfirming] = useState(false);
  const isActive = routing.active?.model.id === model.id;
  const isFallback = routing.fallback?.model.id === model.id;
  const error = test.error ?? activate.error ?? remove.error;
  const tested =
    model.last_tested_at === null
      ? t('serving.llm.never')
      : `${when(model.last_tested_at)}${
          model.latency_ms === null
            ? ''
            : ` · ${t('serving.llm.latency', { ms: model.latency_ms })}`
        }`;
  return (
    <li className="lq-llm__model">
      <div className="lq-llm__model-main">
        <span className="lq-llm__model-name">{model.display_name}</span>
        {isActive && (
          <span className="lq-ai__chip lq-ai__chip--ok">
            {t('serving.llm.model.active')}
          </span>
        )}
        {isFallback && (
          <span className="lq-ai__chip lq-ai__chip--info">
            {t('serving.llm.model.fallback')}
          </span>
        )}
        <span className="lq-ai__mono lq-llm__model-id">
          {model.model_identifier}
          {model.deployment_name !== '' && ` → ${model.deployment_name}`}
        </span>
      </div>
      <div className="lq-llm__model-health">
        <StatusChip status={model.status} />
        <span className="lq-ai__muted">{tested}</span>
      </div>
      <div className="lq-llm__row-actions">
        {!isActive && (
          <Button
            busy={activate.isPending}
            disabled={!canActivate(model, provider)}
            onClick={() => {
              activate.mutate(model.id);
            }}
            size="compact"
            title={
              canActivate(model, provider)
                ? undefined
                : t('serving.llm.model.activateHint')
            }
            tone="primary"
          >
            {t('serving.llm.model.activate')}
          </Button>
        )}
        <OverflowMenu
          items={[
            {
              id: 'test',
              label: t('serving.llm.model.testThis'),
              disabled: test.isPending,
              onSelect: () => {
                test.mutate(model.id);
              },
            },
            {
              id: 'remove',
              label: t('serving.llm.model.removeThis'),
              danger: true,
              disabled: isActive || isFallback,
              onSelect: () => {
                setConfirming(true);
              },
            },
          ]}
          label={t('serving.llm.model.more', { model: model.display_name })}
        />
      </div>
      {/* The status detail only when it says something the chip and latency do not: a connected
          model's detail is "answered in N ms", which the line above already shows. */}
      {model.status !== 'connected' && model.status_detail !== '' && (
        <p className="lq-llm__note lq-llm__model-wide">{model.status_detail}</p>
      )}
      {test.isPending && (
        <p className="lq-llm__note lq-llm__model-wide" role="status">
          {t('serving.llm.model.testing')}
        </p>
      )}
      {test.isSuccess && (
        <div className="lq-llm__model-wide">
          <TestOutcome result={test.data} />
        </div>
      )}
      {error !== null && (
        <p className="lq-llm__error lq-llm__model-wide">{error.message}</p>
      )}
      <ConfirmDialog
        busy={remove.isPending}
        cancelLabel={t('serving.llm.editor.cancel')}
        confirmLabel={t('serving.llm.remove')}
        message={t('serving.llm.model.removeConfirm', { model: model.display_name })}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          remove.mutate(model.id, {
            onSettled: () => {
              setConfirming(false);
            },
          });
        }}
        open={confirming}
        title={t('serving.llm.model.removeTitle')}
        tone="danger"
      />
    </li>
  );
}

/* --- one configured provider ------------------------------------------------------------------- */

/** A saved configuration and its models. The four actions appear only here — an unconfigured
 * provider has nothing to edit, test or delete, so it is offered the form and nothing else. */
function ProviderPanel({
  provider,
  spec,
  routing,
  onEdit,
  onRemoved,
}: {
  readonly provider: ProviderConfigView;
  readonly spec: ProviderSpec;
  readonly routing: RoutingView;
  readonly onEdit: () => void;
  readonly onRemoved: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const test = useTestProvider();
  const remove = useDeleteProvider();
  const [confirming, setConfirming] = useState(false);
  /* Open by default while there is nothing to test or activate: a configuration with no model is
     one step short of being useful, and that step is the obvious next thing to do. */
  const [adding, setAdding] = useState(provider.models.length === 0);
  return (
    <section aria-label={provider.display_name} className="lq-llm__panel">
      <div className="lq-llm__panel-head">
        <div className="lq-llm__panel-title">
          <h3 className="lq-llm__subheading">{provider.display_name}</h3>
          <StatusChip status={provider.status} />
          {!provider.enabled && (
            <strong className="lq-llm__off">{t('serving.llm.disabled')}</strong>
          )}
        </div>
        <div className="lq-llm__actions">
          <Button
            disabled={adding}
            onClick={() => {
              setAdding(true);
            }}
            size="compact"
            tone="primary"
          >
            {t('serving.llm.model.add')}
          </Button>
          <Button onClick={onEdit} size="compact" tone="secondary">
            {t('serving.llm.editConfig')}
          </Button>
          <Button
            busy={test.isPending}
            onClick={() => {
              test.mutate(provider.id);
            }}
            size="compact"
            tone="secondary"
          >
            {t('serving.llm.test')}
          </Button>
          <Button
            onClick={() => {
              setConfirming(true);
            }}
            size="compact"
            tone="danger"
          >
            {t('serving.llm.deleteProvider')}
          </Button>
        </div>
      </div>
      <p className="lq-ai__muted">
        {spec.display_name} · {t(`serving.llm.auth.${provider.auth_method}`)}
        {provider.endpoint_origin !== '' && ` · ${provider.endpoint_origin}`}
        {' · '}
        {provider.credential.configured
          ? t('serving.llm.credential.configured', {
              fields: provider.credential.fields.join(', '),
              when: when(provider.credential.updated_at),
            })
          : t('serving.llm.credential.none')}
      </p>
      {/* A provider test is a test of its first model, and the result is recorded on that model —
          so its line below already says it. Printed here only when there is no model line, where
          the result ("add a model first") has nowhere else to appear. */}
      {test.isSuccess && provider.models.length === 0 && (
        <TestOutcome result={test.data} />
      )}
      {(test.error ?? remove.error) !== null && (
        <Alert title={t('serving.llm.failed')} tone="error">
          {(test.error ?? remove.error)?.message}
        </Alert>
      )}
      {provider.models.length === 0 ? (
        <p className="lq-ai__muted">{t('serving.llm.model.none')}</p>
      ) : (
        <ul aria-label={t('serving.llm.model.listTitle')} className="lq-llm__models">
          {provider.models.map((model) => (
            <ModelRow
              key={model.id}
              model={model}
              provider={provider}
              routing={routing}
            />
          ))}
        </ul>
      )}
      {adding && (
        <AddModel
          onDone={() => {
            setAdding(false);
          }}
          provider={provider}
          spec={spec}
        />
      )}
      <ConfirmDialog
        busy={remove.isPending}
        cancelLabel={t('serving.llm.editor.cancel')}
        confirmLabel={t('serving.llm.deleteProvider')}
        message={t('serving.llm.removeProviderConfirm', {
          provider: provider.display_name,
        })}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          remove.mutate(provider.id, {
            onSuccess: onRemoved,
            onSettled: () => {
              setConfirming(false);
            },
          });
        }}
        open={confirming}
        title={t('serving.llm.removeProviderTitle')}
        tone="danger"
      />
    </section>
  );
}

/* --- provider configuration: one dropdown, one focused panel ----------------------------------- */

/** What the panel under the dropdown shows. `providerId: null` is the create form for `kind`. */
interface Selection {
  readonly kind: ProviderKind;
  readonly providerId: string | null;
  readonly editing: boolean;
}

function ConfiguredSummary({
  providers,
  specs,
  selectedId,
  onManage,
}: {
  readonly providers: readonly ProviderConfigView[];
  readonly specs: readonly ProviderSpec[];
  readonly selectedId: string | null;
  readonly onManage: (provider: ProviderConfigView) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="lq-ai__table-wrap">
      <table
        aria-label={t('serving.llm.configured.title')}
        className="lq-ai__table lq-llm__summary"
      >
        <thead>
          <tr>
            <th>{t('serving.llm.config.name')}</th>
            <th>{t('serving.llm.config.provider')}</th>
            <th>{t('serving.llm.config.models')}</th>
            <th>{t('serving.llm.config.status')}</th>
            <th>
              <span className="lq-visually-hidden">{t('serving.llm.model.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {providers.map((provider) => (
            <tr
              aria-current={provider.id === selectedId ? 'true' : undefined}
              key={provider.id}
            >
              <td>{provider.display_name}</td>
              <td>
                {specs.find((spec) => spec.kind === provider.kind)?.display_name ??
                  provider.kind}
              </td>
              <td>{provider.models.length}</td>
              <td>
                <StatusChip status={provider.status} />
              </td>
              <td className="lq-llm__row-actions">
                <Button
                  disabled={provider.id === selectedId}
                  onClick={() => {
                    onManage(provider);
                  }}
                  size="compact"
                  tone="secondary"
                >
                  {t('serving.llm.config.manage')}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProviderConfiguration({
  specs,
  providers,
  routing,
}: {
  readonly specs: readonly ProviderSpec[];
  readonly providers: readonly ProviderConfigView[];
  readonly routing: RoutingView;
}): ReactElement {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<Selection | null>(null);
  const choices = providerChoices(specs, providers);
  const spec =
    selection === null ? undefined : specs.find((s) => s.kind === selection.kind);
  /* A selected configuration that has since gone (deleted elsewhere) falls back to the create form
     rather than rendering a panel for nothing. */
  const stored =
    selection?.providerId == null
      ? undefined
      : providers.find((provider) => provider.id === selection.providerId);
  const sameKind =
    selection === null ? [] : providers.filter((p) => p.kind === selection.kind);

  const choose = (kind: ProviderKind | ''): void => {
    if (kind === '') {
      setSelection(null);
      return;
    }
    const first = providers.find((provider) => provider.kind === kind);
    setSelection({ kind, providerId: first?.id ?? null, editing: false });
  };

  return (
    <section
      aria-label={t('serving.llm.config.title')}
      className="lq-ai__card lq-llm__section"
    >
      <div className="lq-llm__section-head">
        <h2 className="lq-llm__heading">{t('serving.llm.config.title')}</h2>
        <p className="lq-ai__muted">{t('serving.llm.config.explain')}</p>
      </div>

      {providers.length > 0 && (
        <ConfiguredSummary
          onManage={(provider) => {
            setSelection({
              kind: provider.kind,
              providerId: provider.id,
              editing: false,
            });
          }}
          providers={providers}
          selectedId={stored?.id ?? null}
          specs={specs}
        />
      )}

      <div className="lq-llm__picker">
        <SelectField<ProviderKind | ''>
          label={t('serving.llm.config.select')}
          onChange={choose}
          options={[
            { value: '', label: t('serving.llm.config.placeholder') },
            ...choices.map((choice) => ({
              value: choice.kind,
              group: t(`serving.llm.categoryTitle.${choice.category}`),
              label:
                choice.configured > 0
                  ? t('serving.llm.config.configuredOption', { provider: choice.label })
                  : choice.isDefault
                    ? t('serving.llm.config.defaultOption', { provider: choice.label })
                    : choice.label,
            })),
          ]}
          value={selection?.kind ?? ''}
        />
        {sameKind.length > 1 && stored !== undefined && (
          <SelectField
            label={t('serving.llm.config.which')}
            onChange={(id: string) => {
              if (selection !== null) setSelection({ ...selection, providerId: id });
            }}
            options={sameKind.map((p) => ({ value: p.id, label: p.display_name }))}
            value={stored.id}
          />
        )}
        {selection !== null && stored !== undefined && !selection.editing && (
          <Button
            onClick={() => {
              setSelection({ ...selection, providerId: null, editing: false });
            }}
            tone="secondary"
          >
            {t('serving.llm.config.another', { provider: spec?.display_name ?? '' })}
          </Button>
        )}
      </div>

      {selection === null && (
        <p className="lq-ai__muted">
          {providers.length === 0
            ? t('serving.llm.configured.none')
            : t('serving.llm.config.chooseToManage')}
        </p>
      )}

      {selection !== null && spec !== undefined && stored === undefined && (
        <ProviderEditor
          key={`${spec.kind}:new`}
          onCancel={() => {
            setSelection(null);
          }}
          onSaved={(saved) => {
            setSelection({ kind: saved.kind, providerId: saved.id, editing: false });
          }}
          spec={spec}
          stored={undefined}
        />
      )}

      {selection !== null && spec !== undefined && stored !== undefined && (
        <>
          {selection.editing ? (
            <ProviderEditor
              key={`${spec.kind}:${stored.id}`}
              onCancel={() => {
                setSelection({ ...selection, editing: false });
              }}
              onSaved={() => {
                setSelection({ ...selection, editing: false });
              }}
              spec={spec}
              stored={stored}
            />
          ) : (
            <ProviderPanel
              key={stored.id}
              onEdit={() => {
                setSelection({ ...selection, editing: true });
              }}
              onRemoved={() => {
                setSelection(null);
              }}
              provider={stored}
              routing={routing}
              spec={spec}
            />
          )}
        </>
      )}
    </section>
  );
}

/* --- configuration history --------------------------------------------------------------------- */

const HISTORY_STEP = 10;
/** The audit route's own page ceiling. "Show more" stops here rather than asking for more than one
 * read can return; the full trail stays on the Audit screen. */
const HISTORY_MAX = 200;

function HistoryEntries({
  limit,
  onMore,
}: {
  readonly limit: number;
  readonly onMore: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const providers = useAuditEntries({ aggregateType: 'LlmProvider' }, null, limit);
  const models = useAuditEntries({ aggregateType: 'LlmModel' }, null, limit);
  const routes = useAuditEntries({ action: 'ai.serving.model_activated' }, null, limit);
  if (providers.isPending || models.isPending || routes.isPending) {
    return <SkeletonRows columns={3} rows={3} />;
  }
  if (providers.isError || models.isError || routes.isError) {
    return <p className="lq-ai__muted">{t('serving.llm.history.unreadable')}</p>;
  }
  const entries = [...providers.data.items, ...models.data.items, ...routes.data.items]
    .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at))
    .slice(0, limit);
  if (entries.length === 0) {
    return <p className="lq-ai__muted">{t('serving.llm.history.empty')}</p>;
  }
  /* More exists when any of the three reads was cut short; the merge cannot know beyond that. */
  const more =
    limit < HISTORY_MAX &&
    [providers.data, models.data, routes.data].some((page) => page.nextCursor !== null);
  return (
    <>
      <div className="lq-ai__table-wrap">
        <table aria-label={t('serving.llm.history.title')} className="lq-ai__table">
          <thead>
            <tr>
              <th>{t('serving.llm.history.when')}</th>
              <th>{t('serving.llm.history.action')}</th>
              <th>{t('serving.llm.history.actor')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={`${entry.chain_date}:${String(entry.sequence)}`}>
                <td>{when(entry.occurred_at)}</td>
                <td className="lq-ai__mono">{entry.action}</td>
                <td>{entry.actor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more && (
        <div>
          <Button onClick={onMore} size="compact" tone="secondary">
            {t('serving.llm.history.more')}
          </Button>
        </div>
      )}
    </>
  );
}

/** Collapsed by default, and **its reads are not issued until it is opened** — three audit reads on
 * every visit to a configuration page would spend the audit route's rate budget on a panel most
 * visits never look at. Recording is untouched: this only changes when the trail is read. */
export function ConfigurationHistory(): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(HISTORY_STEP);
  return (
    <section className="lq-ai__card lq-llm__section lq-llm__collapsible">
      <h2 className="lq-llm__heading">
        <button
          aria-controls="lq-llm-history"
          aria-expanded={open}
          className="lq-llm__toggle"
          onClick={() => {
            setOpen((current) => !current);
          }}
          type="button"
        >
          <span aria-hidden="true" className="lq-llm__caret">
            {open ? '▾' : '▸'}
          </span>
          {t('serving.llm.history.title')}
        </button>
      </h2>
      {open && (
        <div className="lq-llm__section" id="lq-llm-history">
          <p className="lq-ai__muted">{t('serving.llm.history.explain')}</p>
          <HistoryEntries
            limit={limit}
            onMore={() => {
              setLimit((current) => Math.min(HISTORY_MAX, current * 2));
            }}
          />
        </div>
      )}
    </section>
  );
}

/* --- the section ------------------------------------------------------------------------------- */

export function ProviderManagement(): ReactElement {
  const { t } = useTranslation();
  const catalog = useProviderCatalog();
  const configs = useProviderConfigs();
  const routing = useRouting();

  const failure = catalog.error ?? configs.error ?? routing.error;
  if (failure !== null) {
    return (
      <Alert title={t('serving.llm.readFailed')} tone="error">
        {failure.message}
      </Alert>
    );
  }
  if (!catalog.isSuccess || !configs.isSuccess || !routing.isSuccess) {
    return <SkeletonRows columns={3} rows={4} />;
  }
  return (
    <div className="lq-llm">
      <ActiveRouting providers={configs.data.items} routing={routing.data} />
      <ProviderConfiguration
        providers={configs.data.items}
        routing={routing.data}
        specs={catalog.data.items}
      />
    </div>
  );
}
